import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Button, Input } from 'antd'
import { SendOutlined } from '@ant-design/icons'
import {
  postRequest,
  dutyConversationMessageUrl,
  successNotification,
  errorNotification
} from '../../../config'

/**
 * A free-form message straight to the customer — no processing, just clarity from support.
 *
 * Used twice on the conversation page (owner, 2026-10-29): under the details, and again under the
 * transcript, where an agent actually is when they decide to reply. Each copy keeps its own draft,
 * so typing in one never fills the other.
 *
 * `compact` drops the explanatory notes for the copy under the transcript: the agent has already
 * read them at the top, and repeating them there would push the reply box off a phone screen.
 */
const MessageComposer = ({ conversationId, appChannel, onSent, compact }) => {
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)

  const send = async () => {
    const text = message.trim()
    if (!text) return
    setSending(true)
    const res = await postRequest(
      dutyConversationMessageUrl(conversationId),
      { text },
      'message',
      () => {}
    )
    setSending(false)
    if (res.success) {
      successNotification('Message sent to the customer')
      setMessage('')
      if (onSent) onSent()
    } else {
      const data = res.error && res.error.response && res.error.response.data
      errorNotification((data && data.detail) || 'Could not send the message')
    }
  }

  return (
    <div>
      {/*
        This box sends over WHATSAPP, whichever channel the conversation came in on —
        `send_text(conv.wa_id, …)` is the only path it has. For an app customer that is a message
        to a number they gave us, in a thread they may never have opened. Said plainly rather than
        disabled: it often IS the right way to reach them, and support should choose knowingly.
      */}
      {appChannel && !compact ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
          message="This customer is on the mobile app"
          description="Messages here go out over WhatsApp, to the number they verified in
            the app. They may not use WhatsApp on it — if this does not land, call them."
        />
      ) : null}
      <Input.TextArea
        rows={compact ? 2 : 2}
        autoSize={compact ? { minRows: 2, maxRows: 6 } : undefined}
        value={message}
        onChange={e => setMessage(e.target.value)}
        onPressEnter={e => {
          // Ctrl/Cmd+Enter sends; a plain Enter is a new line, as in WhatsApp on a computer.
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault()
            send()
          }
        }}
        placeholder="Type a message to send directly to the customer (no processing)…"
        maxLength={1000}
      />
      <div
        style={{
          marginTop: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          flexWrap: 'wrap'
        }}
      >
        <span style={{ color: '#888', fontSize: 12, flex: '1 1 220px' }}>
          {compact
            ? 'Ctrl+Enter to send. Fails if they last messaged over 24h ago.'
            : 'Sent as-is to the customer for clarity. Their reply is still handled by the ' +
              'assistant as usual. (Fails if they last messaged over 24h ago — WhatsApp then ' +
              'needs an approved template.)'}
        </span>
        <Button
          type="primary"
          icon={<SendOutlined />}
          loading={sending}
          disabled={!message.trim()}
          onClick={send}
        >
          Send message
        </Button>
      </div>
    </div>
  )
}

MessageComposer.propTypes = {
  conversationId: PropTypes.string.isRequired,
  appChannel: PropTypes.bool,
  onSent: PropTypes.func,
  compact: PropTypes.bool
}

export default MessageComposer
